import { useState, useRef, useEffect } from 'react';
import { User, onAuthStateChanged } from 'firebase/auth';
import { doc, setDoc, getDoc, deleteDoc, collection, getDocs, addDoc } from "firebase/firestore";
import { db, auth, logout } from '../lib/firebase';
import { isAdminUser } from '../lib/admin';

// Cabeçalho com o token de login real do Firebase (exigido pelas rotas de admin do servidor)
async function adminAuthHeaders(): Promise<Record<string, string>> {
  const idToken = await auth.currentUser?.getIdToken();
  return idToken ? { 'Authorization': `Bearer ${idToken}` } : {};
}

// Marcador local da sessão do admin (não é senha: o acesso de admin é
// sempre conferido pelo login real do Firebase).
export const ADMIN_SESSION_TOKEN = 'ADMIN-SESSION';

export function useAuth() {
  const [authStatus, setAuthStatus] = useState<"idle" | "authenticating" | "authenticated" | "error" | "locked" | "reconnecting">("idle");
  const [authError, setAuthError] = useState("");
  const [authToken, setAuthToken] = useState(() => localStorage.getItem("flowaes_token") || "");
  const [isAuthLoading, setIsAuthLoading] = useState<boolean>(true);
  const [hasAttemptedAutoLogin, setHasAttemptedAutoLogin] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState<boolean>(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showLoginLogoutConfirm, setShowLoginLogoutConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [isFirestoreOffline, setIsFirestoreOffline] = useState(false);
  const [leadsList, setLeadsList] = useState<any[]>([]);
  const [isLeadsLoading, setIsLeadsLoading] = useState(false);
  const [feedbacksList, setFeedbacksList] = useState<any[]>([]);
  const [expandedFeedbackId, setExpandedFeedbackId] = useState<string | null>(null);
  const [isFeedbacksLoading, setIsFeedbacksLoading] = useState(false);
  const [feedbackToDelete, setFeedbackToDelete] = useState<string | null>(null);
  const [systemUsersList, setSystemUsersList] = useState<any[]>([]);
  const [isSystemUsersLoading, setIsSystemUsersLoading] = useState(false);
  const [systemUsersFilter, setSystemUsersFilter] = useState('');
  const [userLevelFilter, setUserLevelFilter] = useState<'all' | 'leads' | 'active' | 'blocked'>('all');
  const [showUserFilters, setShowUserFilters] = useState(false);
  const [editingSystemUser, setEditingSystemUser] = useState<any | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ title: string, message: string, onConfirm: () => void } | null>(null);
  const [expandedSystemUserId, setExpandedSystemUserId] = useState<string | null>(null);
  const [activeSystemUserMenu, setActiveSystemUserMenu] = useState<string | null>(null);
  const [showActivitiesForUserId, setShowActivitiesForUserId] = useState<string | null>(null);
  const [userFeedbacksModalId, setUserFeedbacksModalId] = useState<string | null>(null);
  const [leadName, setLeadName] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadCompany, setLeadCompany] = useState('');
  const [leadDetails, setLeadDetails] = useState('');
  const [isSubmittingLead, setIsSubmittingLead] = useState(false);
  const [leadSuccessMsg, setLeadSuccessMsg] = useState<string | null>(null);
  const [isSavingBrand, setIsSavingBrand] = useState(false);
  const [brandSaveSuccess, setBrandSaveSuccess] = useState(false);
  const [brandSaveError, setBrandSaveError] = useState("");
  const [adminMetrics, setAdminMetrics] = useState({ onlineUsers: 0, totalUsers: 0, activeLicenses: 0 });
  const wsRef = useRef<WebSocket | null>(null);

  // Fecha o WebSocket de licença atual sem disparar reconexão/erros atrasados
  const closeLicenseSocket = () => {
    const ws = wsRef.current;
    if (!ws) return;
    wsRef.current = null;
    ws.onopen = null;
    ws.onmessage = null;
    ws.onerror = null;
    ws.onclose = null;
    try { ws.close(); } catch (e) { /* ignore */ }
  };
    useEffect(() => {
        if (isAuthLoading) return;
        if (hasAttemptedAutoLogin) return;
        if (authStatus === "authenticated" || authStatus === "authenticating") {
          setHasAttemptedAutoLogin(true);
          return;
        }
        if (authStatus === "idle" && authToken) {
          setHasAttemptedAutoLogin(true);
          handleLogin(authToken);
        } else if (authStatus === "idle" && !authToken) {
          setHasAttemptedAutoLogin(true);
        }
      }, [authStatus, authToken, isAuthLoading, hasAttemptedAutoLogin]);

    const handleLogin = async (token: string, force = false, silent = false) => {
        if (isAdminUser(user) || isAdminUser(auth.currentUser)) {
          closeLicenseSocket();
          setAuthStatus("authenticated");
          localStorage.setItem("flowaes_token", ADMIN_SESSION_TOKEN);
          setAuthToken(ADMIN_SESSION_TOKEN);
          setIsSubscriptionActive(true);
          setIsAuthLoading(false);
          return;
        }

        // O marcador de sessão do admin não é uma licença: nunca enviar ao servidor.
        // (Acontece se a página carregar antes de o Firebase reconhecer a conta.)
        if (token === ADMIN_SESSION_TOKEN) {
          localStorage.removeItem("flowaes_token");
          setAuthToken("");
          return;
        }

        if (wsRef.current) {
          wsRef.current.onopen = null;
          wsRef.current.onmessage = null;
          wsRef.current.onerror = null;
          wsRef.current.onclose = null;
          try {
            wsRef.current.close();
          } catch (e) {
            console.error("Error closing old WebSocket:", e);
          }
          wsRef.current = null;
        }

        if (!silent) {
          setAuthStatus("authenticating");
        }
        setAuthError("");
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        const wsUrl = `${protocol}//${window.location.host}/ws/auth`;
        
        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        // Ignora eventos de um socket antigo que já foi substituído ou fechado
        const isStale = () => wsRef.current !== ws;

        ws.onerror = (err) => {
          console.error("[WS Auth Error]", err, "wsUrl was:", wsUrl, "readyState:", ws.readyState);
        };

        const timeoutId = setTimeout(() => {
          if (isStale()) return;
          setAuthStatus((prev) => {
             if (prev === "authenticating" && !silent) {
                ws.close();
                setAuthError("Tempo de resposta esgotado. O Nó Seguro demorou muito para responder (possível lentidão no banco de dados). Tente novamente.");
                return "error";
             }
             return prev;
          });
        }, 15000);

        ws.onopen = async () => {
          const emailToSend = user?.email || auth.currentUser?.email || "";
          const uidToSend = user?.uid || auth.currentUser?.uid || "";
          // Token de login do Firebase: permite ao servidor reconhecer o admin com segurança
          const idToken = await auth.currentUser?.getIdToken().catch(() => undefined);
          if (isStale()) return;
          ws.send(JSON.stringify({ 
            type: force ? "force_authenticate" : "authenticate", 
            token, 
            email: emailToSend, 
            uid: uidToSend,
            idToken
          }));
        };

        ws.onmessage = (event) => {
          clearTimeout(timeoutId);
          if (isStale()) return;
          try {
            const data = JSON.parse(event.data);
            if (data.type === "authenticated") {
              setAuthStatus("authenticated");
              localStorage.setItem("flowaes_token", token);
              setAuthToken(token);
            } else if (data.type === "error") {
              if (silent && (data.message.includes("banco de dados") || data.message.includes("Sincronizando"))) {
                 console.warn("Ignorando erro temporário durante reconexão silenciosa:", data.message);
                 setAuthStatus("authenticated");
              } else {
                 setAuthStatus("error");
                 setAuthError(data.message);
                 if (!silent) {
                   localStorage.removeItem("flowaes_token");
                 }
              }

            } else if (data.type === "locked_warning") {
              setAuthStatus("locked");
              setAuthError(data.message);
            }
          } catch (e) {
            console.error("WS parse error", e);
          }
        };

        ws.onclose = () => {
          clearTimeout(timeoutId);
          if (isStale()) return;
          wsRef.current = null;
          // Reconexão Silenciosa
          setAuthStatus(prev => {
            if (prev === "authenticated" || prev === "reconnecting") {
              setTimeout(() => {
                 // Só reconecta se nenhum outro socket/login tomou o lugar deste
                 if (wsRef.current === null && localStorage.getItem("flowaes_token") === token) {
                   console.log("Reconectando silenciosamente...");
                   handleLogin(token, true, true); // Force just in case it thinks we are still there, and silent
                 }
              }, 3000);
              return "reconnecting"; // Adiciona estado visual discreto se quiser, mas mantendo na UI
            }
            return prev === "authenticating" && !silent ? "error" : prev;
          });
        };
      };



      const handleLogout = () => {
          closeLicenseSocket();
          logout();
          setAuthStatus("idle");
          setAuthToken("");
          setAuthError("");
          localStorage.removeItem("flowaes_token");
          setIsSubscriptionActive(false);
          setShowLoginLogoutConfirm(false);
          setShowLogoutConfirm(false);
        };

  const fetchFeedbacks = async () => {
    setIsFeedbacksLoading(true);
    try {
      const snap = await getDocs(collection(db, 'feedbacks'));
      const list: any[] = [];
      snap.forEach(docSnap => {
        list.push({ id: docSnap.id, ...docSnap.data() });
      });
      // Sort by descending createdAt
      list.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
      });
      setFeedbacksList(list);
    } catch (e) {
      console.error("Error fetching feedbacks:", e);
    } finally {
      setIsFeedbacksLoading(false);
    }
  };

  const confirmDeleteFeedback = async () => {
    if (!feedbackToDelete) return;
    try {
      await deleteDoc(doc(db, 'feedbacks', feedbackToDelete));
      setFeedbacksList(prev => prev.filter(f => f.id !== feedbackToDelete));
      if (expandedFeedbackId === feedbackToDelete) setExpandedFeedbackId(null);
    } catch (error) {
      console.error("Error deleting feedback:", error);
    } finally {
      setFeedbackToDelete(null);
    }
  };

  const fetchSystemUsers = async () => {
    setIsSystemUsersLoading(true);
    try {
      const snap = await getDocs(collection(db, 'users'));
      const list: any[] = [];
      snap.forEach(docSnap => {
        list.push({ uid: docSnap.id, ...docSnap.data() });
      });
      setSystemUsersList(list);
    } catch (e) {
      console.error("Error fetching system users:", e);
    } finally {
      setIsSystemUsersLoading(false);
    }
  };

  const fetchLeads = async () => {
    setIsLeadsLoading(true);
    try {
      // Leads ficam no Firestore (coleção "leads"); só o admin pode ler
      const snap = await getDocs(collection(db, 'leads'));
      const list: any[] = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      list.sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
      setLeadsList(list);
    } catch (e) {
      console.error("Error fetching leads:", e);
    } finally {
      setIsLeadsLoading(false);
    }
  };

  const handleUpdateUserStatus = async (uid: string, newStatus: string, userToRevoke?: any) => {
    
    // Helper function to update user status and prepend an activity
    const updateStatusAndLogActivity = async (targetUid: string, statusValue: string, activityDesc: string) => {
      const userRef = doc(db, 'users', targetUid);
      const userSnap = await getDoc(userRef);
      const existingActivities = userSnap.exists() ? (userSnap.data().activities || []) : [];
      const newActivity = {
        type: 'status_change',
        description: activityDesc,
        timestamp: new Date().toISOString(),
        by: 'Administrador (GOD)'
      };
      
      const updateObj: any = { 
        subscription_status: statusValue,
        activities: [newActivity, ...existingActivities]
      };
      await setDoc(userRef, updateObj, { merge: true });
    };

    // 1. REVOGAR (updating from active to pending, which puts them back in the wait list/leads)
    if (newStatus === 'pending' && userToRevoke?.subscription_status === 'active') {
      setConfirmDialog({
        title: 'Revogar Acesso',
        message: 'Tem certeza que deseja revogar o acesso deste usuário? Ele perderá a licença e voltará para a lista de leads (espera).',
        onConfirm: async () => {
          try {
            await updateStatusAndLogActivity(uid, 'pending', 'Acesso revogado manualmente pelo Administrador. Retornado à lista de espera.');
            await fetchSystemUsers();
            await fetchLeads();
          } catch (e: any) {
            console.error("Erro ao revogar:", e);
            alert("Erro ao revogar: Você precisa estar logado com a conta de admin. " + e.message);
          }
          setConfirmDialog(null);
        }
      });
      return;
    }
    
    // 2. BLOQUEAR (updating to blocked)
    if (newStatus === 'blocked') {
      const isCurrentlyActive = userToRevoke?.subscription_status === 'active';
      const title = isCurrentlyActive ? 'Revogar e Bloquear' : 'Bloquear Usuário';
      const message = isCurrentlyActive
        ? 'Tem certeza que deseja revogar a licença e bloquear o acesso deste usuário?'
        : 'Tem certeza que deseja bloquear este usuário?';
        
      setConfirmDialog({
        title,
        message,
        onConfirm: async () => {
          try {
            const logMsg = isCurrentlyActive 
              ? 'Acesso revogado e usuário bloqueado manualmente pelo Administrador.' 
              : 'Usuário bloqueado manualmente pelo Administrador.';
            await updateStatusAndLogActivity(uid, 'blocked', logMsg);
            await fetchSystemUsers();
            await fetchLeads();
          } catch (e: any) {
            console.error("Erro ao bloquear:", e);
            alert("Erro ao bloquear: " + e.message);
          }
          setConfirmDialog(null);
        }
      });
      return;
    }
    
    // 3. Tornar Colaborador (Staff)
    if (newStatus === 'staff') {
      setConfirmDialog({
        title: 'Tornar Colaborador (Staff)',
        message: 'Tem certeza que deseja conceder acesso de Colaborador a este usuário? O acesso dele ao terminal será liberado, mas ele não terá acesso ao Painel GOD.',
        onConfirm: async () => {
          try {
            const userRef = doc(db, 'users', uid);
            const userSnap = await getDoc(userRef);
            const existingActivities = userSnap.exists() ? (userSnap.data().activities || []) : [];
            const newActivity = {
              type: 'role_change',
              description: 'Promovido a Colaborador (Staff) pelo Administrador.',
              timestamp: new Date().toISOString(),
              by: 'Administrador (GOD)'
            };
            
            const updateObj: any = { 
              role: 'staff',
              subscription_status: 'active',
              activities: [newActivity, ...existingActivities]
            };
            await setDoc(userRef, updateObj, { merge: true });
            await fetchSystemUsers();
          } catch (e: any) {
            console.error("Erro ao promover a staff:", e);
            alert("Erro ao promover: " + e.message);
          }
          setConfirmDialog(null);
        }
      });
      return;
    }
    
    // 4. Revogar Colaborador (Remover Staff)
    if (newStatus === 'remove_staff') {
      setConfirmDialog({
        title: 'Remover Acesso de Colaborador',
        message: 'Tem certeza que deseja remover o acesso de Colaborador deste usuário? Ele deixará de ser Colaborador.',
        onConfirm: async () => {
          try {
            const userRef = doc(db, 'users', uid);
            const userSnap = await getDoc(userRef);
            const existingActivities = userSnap.exists() ? (userSnap.data().activities || []) : [];
            const newActivity = {
              type: 'role_change',
              description: 'Acesso de Colaborador removido pelo Administrador.',
              timestamp: new Date().toISOString(),
              by: 'Administrador (GOD)'
            };
            
            const updateObj: any = { 
              role: 'user',
              activities: [newActivity, ...existingActivities]
            };
            await setDoc(userRef, updateObj, { merge: true });
            await fetchSystemUsers();
          } catch (e: any) {
            console.error("Erro ao remover staff:", e);
            alert("Erro ao remover: " + e.message);
          }
          setConfirmDialog(null);
        }
      });
      return;
    }

    // 5. Outras atualizações de status
    try {
      const isCurrentlyBlocked = userToRevoke?.subscription_status === 'blocked';
      let logMsg = `Status de acesso atualizado para: ${newStatus}`;
      if (newStatus === 'active') {
        logMsg = isCurrentlyBlocked ? 'Usuário desbloqueado e acesso ativado pelo Administrador.' : 'Acesso ativado manualmente pelo Administrador.';
      } else if (newStatus === 'pending') {
        logMsg = isCurrentlyBlocked ? 'Usuário desbloqueado e retornado à lista de espera pelo Administrador.' : 'Usuário retornado à lista de espera pelo Administrador.';
      }
      
      await updateStatusAndLogActivity(uid, newStatus, logMsg);
      await fetchSystemUsers();
    } catch (e: any) {
      console.error("Erro ao atualizar status:", e);
      alert("Erro ao atualizar: Você precisa estar logado com a conta de admin. " + e.message);
    }
  };

  const handleDeleteUser = async (uid: string) => {
    
    setConfirmDialog({
      title: 'Excluir Usuário',
      message: 'Tem certeza que deseja excluir este usuário permanentemente?',
      onConfirm: async () => {
        try {
          // 1) Apaga o cadastro no Firestore
          await deleteDoc(doc(db, "users", uid));
          // 2) Em seguida, apaga a conta de login (Firebase Auth) pelo servidor
          let authDeleted = false;
          let authError = '';
          try {
            const res = await fetch(`/api/users/${encodeURIComponent(uid)}`, {
              method: 'DELETE',
              headers: await adminAuthHeaders()
            });
            const data = await res.json().catch(() => ({}));
            authDeleted = res.ok && data.authDeleted === true;
            authError = data.error || '';
          } catch (authErr: any) {
            authError = authErr?.message || 'Falha de rede';
          }
          await fetchSystemUsers();
          if (!authDeleted) {
            alert("Cadastro excluído, mas a conta de login NÃO foi apagada" + (authError ? ` (${authError})` : "") + ". Se a pessoa entrar de novo, um cadastro novo (pendente de aprovação) será criado automaticamente.");
          }
        } catch (e: any) {
          console.error("Erro ao excluir usuário:", e);
          alert("Erro ao excluir: Você precisa estar logado com a conta de admin. " + e.message);
        }
        setConfirmDialog(null);
      }
    });
  };

  const handleDeleteLead = async (id: string) => {
    setConfirmDialog({
      title: 'Excluir Lead',
      message: 'Tem certeza que deseja excluir este lead permanentemente da lista de espera?',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'leads', String(id)));
          await fetchLeads();
        } catch (e: any) {
          console.error("Erro ao excluir lead:", e);
          alert("Erro ao excluir lead: " + e.message);
        }
        setConfirmDialog(null);
      }
    });
  };

  const handleSaveUserEdit = async () => {
    if (!editingSystemUser) return;

    if (editingSystemUser.type === 'lead') {
      try {
        await setDoc(doc(db, 'leads', String(editingSystemUser.id)), {
          name: editingSystemUser.name || '',
          email: editingSystemUser.email || '',
          phone: editingSystemUser.phone || '',
          company: editingSystemUser.company || '',
          details: editingSystemUser.details || ''
        }, { merge: true });
        setEditingSystemUser(null);
        await fetchLeads();
      } catch (e: any) {
        console.error("Erro ao editar lead:", e);
        alert("Erro ao editar lead: " + e.message);
      }
      return;
    }

    
    try {
      const { uid, ...data } = editingSystemUser;
      
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      const existingActivities = userSnap.exists() ? (userSnap.data().activities || []) : [];
      const newActivity = {
        type: 'profile_edit',
        description: 'Dados cadastrais atualizados manualmente pelo Administrador.',
        timestamp: new Date().toISOString(),
        by: 'Administrador (GOD)'
      };
      
      const updateObj: any = { 
        ...data,
        activities: [newActivity, ...existingActivities]
      };
      await setDoc(userRef, updateObj, { merge: true });
      setEditingSystemUser(null);
      await fetchSystemUsers();
    } catch (e: any) {
      console.error("Erro ao salvar edição:", e);
      alert("Erro ao salvar: Você precisa estar logado com a conta de admin. " + e.message);
    }
  };

  const submitLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadName || !leadEmail || !leadPhone) {
      alert("Por favor, preencha os campos obrigatórios (Nome, E-mail e Telefone).");
      return;
    }
    setIsSubmittingLead(true);
    setLeadSuccessMsg(null);
    try {
      await addDoc(collection(db, 'leads'), {
        name: leadName.slice(0, 200),
        email: leadEmail.slice(0, 254),
        phone: leadPhone.slice(0, 50),
        company: (leadCompany || '').slice(0, 200),
        details: (leadDetails || '').slice(0, 2000),
        createdAt: new Date().toISOString()
      });
      setLeadSuccessMsg("Inscrição realizada com sucesso! Nosso time entrará em contato em breve.");
      setLeadName('');
      setLeadEmail('');
      setLeadPhone('');
      setLeadCompany('');
      setLeadDetails('');
      if (isAdminUser(auth.currentUser)) await fetchLeads();
    } catch (err) {
      console.error(err);
      alert("Ocorreu um erro de rede. Tente novamente.");
    } finally {
      setIsSubmittingLead(false);
    }
  };

  const fetchAdminMetrics = async () => {
    try {
      const res = await fetch('/api/admin/metrics', { headers: await adminAuthHeaders() });
      let onlineUsers = 0;
      if (res.ok) {
        const data = await res.json();
        onlineUsers = data.activeConnections || 0;
      }
      let totalUsers = 0;
      let activeLicenses = 0;
      const usersSnap = await getDocs(collection(db, 'users'));
      totalUsers = usersSnap.size;
      usersSnap.forEach(docSnap => {
        if (docSnap.data().subscription_status === 'active' || docSnap.data().role === 'staff') {
          activeLicenses++;
        }
      });
      setAdminMetrics({ onlineUsers, totalUsers, activeLicenses });
    } catch (e) {
      console.error("Error fetching admin metrics:", e);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const isAdmin = isAdminUser(currentUser);
        if (isAdmin) {
          // Admin não usa licença: encerra qualquer validação de licença em andamento
          closeLicenseSocket();
          setAuthToken(ADMIN_SESSION_TOKEN);
          localStorage.setItem('flowaes_token', ADMIN_SESSION_TOKEN);
          setAuthStatus('authenticated');
          setIsSubscriptionActive(true);
        }
        setIsAuthLoading(false);
      } else {
        setIsAuthLoading(false);
        setIsSubscriptionActive(false);
        setAuthStatus('idle');
      }
    });
    return () => unsubscribe();
  }, []);

  return {
    authStatus, setAuthStatus, authError, setAuthError, authToken, setAuthToken, isAuthLoading, setIsAuthLoading, hasAttemptedAutoLogin, setHasAttemptedAutoLogin, user, setUser, isSubscriptionActive, setIsSubscriptionActive, showLogoutConfirm, setShowLogoutConfirm, showLoginLogoutConfirm, setShowLoginLogoutConfirm, isSaving, setIsSaving, saveMessage, setSaveMessage, isFirestoreOffline, setIsFirestoreOffline, leadsList, setLeadsList, isLeadsLoading, setIsLeadsLoading, feedbacksList, setFeedbacksList, expandedFeedbackId, setExpandedFeedbackId, isFeedbacksLoading, setIsFeedbacksLoading, feedbackToDelete, setFeedbackToDelete, systemUsersList, setSystemUsersList, isSystemUsersLoading, setIsSystemUsersLoading, systemUsersFilter, setSystemUsersFilter, userLevelFilter, setUserLevelFilter, showUserFilters, setShowUserFilters, editingSystemUser, setEditingSystemUser, confirmDialog, setConfirmDialog, expandedSystemUserId, setExpandedSystemUserId, activeSystemUserMenu, setActiveSystemUserMenu, showActivitiesForUserId, setShowActivitiesForUserId, userFeedbacksModalId, setUserFeedbacksModalId, leadName, setLeadName, leadEmail, setLeadEmail, leadPhone, setLeadPhone, leadCompany, setLeadCompany, leadDetails, setLeadDetails, isSubmittingLead, setIsSubmittingLead, leadSuccessMsg, setLeadSuccessMsg, isSavingBrand, setIsSavingBrand, brandSaveSuccess, setBrandSaveSuccess, brandSaveError, setBrandSaveError, adminMetrics, setAdminMetrics, wsRef, handleLogin,
    handleLogout: handleLogout,
    fetchFeedbacks,
    confirmDeleteFeedback,
    fetchSystemUsers,
    fetchLeads,
    handleUpdateUserStatus,
    handleDeleteUser,
    handleDeleteLead,
    handleSaveUserEdit,
    submitLead,
    fetchAdminMetrics,
  };
}
